import React, { useState } from 'react';
import axios from 'axios';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const exempt = user => user.membership_payment_waived === true ||
  (user.membership?.status === 'exempt' && user.membership?.reason === 'approved_before_membership_launch');

export const MembershipApprovalButton = ({ user, onApprove, disabled, prefix = 'admin' }) => (
  <Button size="sm" data-testid={`${prefix}-approve-user-${user.id}`}
    disabled={disabled || user.banned || (user.approved && user.account_approval !== 'pending')} onClick={() => onApprove(user)}
    title={user.banned ? 'Unban this user before approving the account' : user.approved ? 'Account already approved' : 'Approve free basic access'}
    className="bg-cyan-700 hover:bg-cyan-600 text-white transition-colors">
    <CheckCircle2 className="w-4 h-4 mr-2 shrink-0" />
    {user.approved && user.account_approval !== 'pending' ? 'Approved' : 'Approve account'}
  </Button>
);

export const MembershipExemptionStatus = ({ user }) => {
  if (!exempt(user)) return null;
  return <div className="mt-2 text-xs [overflow-wrap:anywhere]">
    <p className="text-cyan-300" data-testid={`admin-membership-exemption-${user.id}`}>
      {user.membership_payment_waived ? 'Premium fees waived · Permanent' : 'Existing member · Payment exempt'}
    </p>
    {user.membership_payment_waived_at && <p className="text-slate-400 mt-1" data-testid={`admin-membership-approval-audit-${user.id}`}>
      Approved by {user.membership_payment_waived_by_name || user.membership_payment_waived_by}
      {' · '}{new Date(user.membership_payment_waived_at).toLocaleString()}
    </p>}
  </div>;
};

export const MembershipApprovalDialog = ({ user, onClose, onApproved }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const approve = async () => {
    if (busy) return;
    setBusy(true); setError('');
    try {
      const { data } = await axios.post(`${API}/admin/approve-user/${encodeURIComponent(user.id)}`);
      onApproved(data);
    } catch (exception) {
      setError(exception.response?.data?.detail || 'Approval could not be saved. Please try again.');
    } finally { setBusy(false); }
  };
  return <Dialog open={Boolean(user)} onOpenChange={open => { if (!open && !busy) onClose(); }}>
    <DialogContent data-testid="admin-approve-user-dialog" closeTestId="admin-approve-user-close"
      className="w-[calc(100%-2rem)] max-w-lg bg-slate-900 border-slate-700 text-slate-100 rounded-lg">
      <DialogHeader>
        <DialogTitle data-testid="admin-approve-user-title">Approve this account?</DialogTitle>
        <DialogDescription className="text-slate-300" data-testid="admin-approve-user-description">
          This member will be able to sign in with free basic access. This does not subscribe them, waive Premium fees, or enable autonomous trading. Ban and delete controls remain available.
        </DialogDescription>
      </DialogHeader>
      <div className="min-w-0 [overflow-wrap:anywhere]" data-testid="admin-approve-user-identity">
        <p className="font-medium">{user?.name || user?.username}</p><p className="text-sm text-slate-400">{user?.email}</p>
      </div>
      {error && <p role="alert" className="text-sm text-red-300" data-testid="admin-approve-user-error">{error}</p>}
      <DialogFooter className="gap-2">
        <Button variant="outline" disabled={busy} onClick={onClose} data-testid="admin-approve-user-cancel">Cancel</Button>
        <Button disabled={busy} onClick={approve} className="bg-cyan-700 hover:bg-cyan-600 text-white" data-testid="admin-approve-user-confirm">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
          {busy ? 'Approving…' : 'Approve free basic access'}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>;
};